import React, { useMemo } from 'react';
import { Box, Grid, Typography, Card, CardContent, Avatar, Rating } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { colors } from './Theme/WebTheme';
import { FONT_FAMILY } from '../common/sharedFunctions';
import MobileSlider from './MobileSlider';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import testimonialImage from '../assets/img/White Yellow Modern Business Quote LinkedIn Post (Publication Instagram) (3).png';

const TestimonialsSection = React.memo(() => {
  const { i18n } = useTranslation();

  const langCode = useMemo(() => 
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'fr'),
    [i18n?.language]
  );

  const testimonialsContent = useMemo(() => ({
    fr: {
      title: 'Ils ont adopté notre application',
      testimonials: [
        {
          name: 'Sophie Martin',
          text: 'L\'application Allô Driver me permet de préparer mes trajets plus simplement : tarif affiché avant validation, suivi de course et réservation depuis le téléphone.',
          rating: 5,
          avatar: 'SM'
        },
        {
          name: 'Pierre Dubois',
          text: 'En tant que professionnel, j\'utilise l\'app quotidiennement pour mes déplacements. Les véhicules hybrides sont impeccables, les tarifs transparents et très compétitifs. Le suivi en temps réel est un vrai plus. Une application moderne qui respecte mon budget ET l\'environnement.',
          rating: 5,
          avatar: 'PD'
        },
        {
          name: 'Marie Lefebvre',
          text: 'J\'apprécie la possibilité de choisir un service VTC local avec des véhicules hybrides ou électriques selon disponibilité. L\'application reste claire pour suivre mes trajets.',
          rating: 5,
          avatar: 'ML'
        }
      ]
    },
    en: {
      title: 'They adopted our application',
      testimonials: [
        {
          name: 'Sophie Martin',
          text: 'The Allô Driver app changed the way I travel! Booking in 30 seconds, fare clearly displayed upfront, and a silent electric Tesla vehicle. No more invoice surprises. The best value for money in Toulouse!',
          rating: 5,
          avatar: 'SM'
        },
        {
          name: 'Pierre Dubois',
          text: 'As a professional, I use the app daily for my trips. The hybrid vehicles are impeccable, the rates transparent and very competitive. Real-time tracking is a real plus. A modern app that respects my budget AND the environment.',
          rating: 5,
          avatar: 'PD'
        },
        {
          name: 'Marie Lefebvre',
          text: 'I favor eco-responsible services and Allô Driver checks all the boxes: 100% hybrid/electric fleet, rates 20% cheaper than elsewhere, and an ultra-intuitive app. My Montauban-Toulouse transfers in Tesla have become a real ecological pleasure!',
          rating: 5,
          avatar: 'ML'
        }
      ]
    }
  }), []);

  const content = useMemo(() => 
    testimonialsContent[langCode] || testimonialsContent.fr,
    [testimonialsContent, langCode]
  );

  return (
    <Box sx={{ 
      py: { xs: 6, md: 10 }, 
      px: { xs: 2, md: 4 },
      backgroundColor: 'transparent',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Background decoration */}
      <Box sx={{
        position: 'absolute',
        bottom: '-30%',
        left: '-15%',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: `radial-gradient(circle, rgba(212, 175, 55, 0.08) 0%, transparent 70%)`,
        pointerEvents: 'none'
      }} />

      <Box sx={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        {/* Header with Image */}
        <Grid container spacing={4} alignItems="center" sx={{ mb: 6 }}>
          <Grid item xs={12} md={8}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontFamily: FONT_FAMILY,
                fontWeight: 700,
                fontSize: { xs: '32px', md: '48px' },
                color: '#fff',
                mb: 2,
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              {content.title}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              component="img"
              src={testimonialImage}
              alt="Témoignages clients"
              sx={{
                width: '100%',
                height: 'auto',
                maxWidth: '300px',
                margin: { xs: '0 auto', md: '0 0 0 auto' },
                display: 'block',
                borderRadius: '16px',
                boxShadow: '0 10px 40px rgba(212, 175, 55, 0.3)'
              }}
            />
          </Grid>
        </Grid>

        {/* Testimonials Grid - Desktop */}
        <Grid container spacing={3} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          {content.testimonials.map((testimonial, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderRadius: '20px',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'visible',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    borderColor: colors.Button_Primary,
                    boxShadow: `0 16px 48px rgba(212, 175, 55, 0.25)`,
                    backgroundColor: 'rgba(212, 175, 55, 0.08)'
                  }
                }}
              >
                {/* Quote icon */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: 24,
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: colors.Button_Primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 24px rgba(212, 175, 55, 0.4)`
                }}>
                  <FormatQuoteIcon sx={{ color: '#000', fontSize: 24 }} />
                </Box>

                <CardContent sx={{ p: 4, pt: 5 }}>
                  {/* Rating */}
                  <Box sx={{ mb: 2 }}>
                    <Rating 
                      value={testimonial.rating} 
                      readOnly 
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: colors.Button_Primary
                        }
                      }}
                    />
                  </Box>

                  {/* Testimonial Text */}
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '15px',
                      lineHeight: 1.7,
                      mb: 3,
                      fontStyle: 'italic'
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>

                  {/* Author */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: colors.Button_Primary,
                        color: '#000',
                        fontWeight: 700,
                        width: 48,
                        height: 48
                      }}
                    >
                      {testimonial.avatar}
                    </Avatar>
                    <Box>
                      <Typography 
                        sx={{ 
                          fontFamily: FONT_FAMILY,
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '16px'
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontFamily: FONT_FAMILY,
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: '13px'
                        }}
                      >
                        Client vérifié
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Mobile Slider */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 4 }}>
          <MobileSlider spacing={3}>
            {content.testimonials.map((testimonial, index) => (
              <Card 
                key={index}
                sx={{ 
                  height: '100%',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(212, 175, 55, 0.2)',
                  borderRadius: '20px',
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                {/* Quote icon */}
                <Box sx={{
                  position: 'absolute',
                  top: -20,
                  left: 24,
                  width: 50,
                  height: 50,
                  borderRadius: '50%',
                  backgroundColor: colors.Button_Primary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 8px 24px rgba(212, 175, 55, 0.4)`
                }}>
                  <FormatQuoteIcon sx={{ color: '#000', fontSize: 24 }} />
                </Box>

                <CardContent sx={{ p: 3, pt: 6 }}>
                  {/* Rating */}
                  <Box sx={{ mb: 2 }}>
                    <Rating 
                      value={testimonial.rating} 
                      readOnly 
                      sx={{
                        '& .MuiRating-iconFilled': {
                          color: colors.Button_Primary
                        }
                      }}
                    />
                  </Box>

                  {/* Testimonial Text */}
                  <Typography 
                    sx={{ 
                      fontFamily: FONT_FAMILY,
                      color: 'rgba(255,255,255,0.9)',
                      fontSize: '15px',
                      lineHeight: 1.7,
                      mb: 3,
                      fontStyle: 'italic'
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>

                  {/* Author */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: colors.Button_Primary,
                        color: '#000',
                        fontWeight: 700,
                        width: 48,
                        height: 48
                      }}
                    >
                      {testimonial.avatar}
                    </Avatar>
                    <Box>
                      <Typography 
                        sx={{ 
                          fontFamily: FONT_FAMILY,
                          fontWeight: 700,
                          color: '#fff',
                          fontSize: '16px'
                        }}
                      >
                        {testimonial.name}
                      </Typography>
                      <Typography 
                        sx={{ 
                          fontFamily: FONT_FAMILY,
                          color: 'rgba(255,255,255,0.6)',
                          fontSize: '13px'
                        }}
                      >
                        Client vérifié
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </MobileSlider>
        </Box>
      </Box>
    </Box>
  );
});

export default TestimonialsSection;
